/**
 * Eyes-closed veil (components/AudioPlayer/spec.md § Eyes-closed mode). No wake lock, no
 * Fullscreen API, no time, no text beyond `[Paused]` — the screen may sleep, it is bedtime
 * (decisions.md §23).
 */
import {useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {Icon} from '@ui/Icon/icon'
import {PointOfLight} from '@product/quies/components/PointOfLight/PointOfLight'
import {useInterfaceStrings} from '@product/quies/i18n/catalogue'
import styles from './EyesClosed.module.scss'

interface EyesClosedProps {
    paused: boolean
    ended: boolean
    onToggle: () => void
    onExit: () => void
}

export function EyesClosed({paused, ended, onToggle, onExit}: EyesClosedProps) {
    const {t} = useInterfaceStrings()
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        buttonRef.current?.focus()
    }, [])

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onExit()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onExit])

    // Once the meditation has ended there is nothing left to toggle — any touch lifts the veil
    // onto the ended player and Continue (spec: "End of the meditation").
    const handleActivate = () => (ended ? onExit() : onToggle())

    // On <body>, outside the page the host makes inert while veiled.
    return createPortal(
        <div className={styles.veil}>
            <button
                ref={buttonRef}
                type="button"
                className={styles.surface}
                aria-label={ended ? t.player.openEyes : paused ? t.player.play : t.player.pause}
                onClick={handleActivate}
            >
                <span className={ended ? styles.endedLight : undefined} style={ended ? {animationDuration: '60s'} : undefined}>
                    {/* Dim while it plays too (spec: "the light stays at 35%, breathing"). */}
                    <PointOfLight size="session" dim breathing={!paused && !ended} />
                </span>
                {paused && !ended && <span className={styles.pausedLabel}>{t.player.paused}</span>}
            </button>

            <button type="button" className={styles.exit} aria-label={t.player.openEyes} onClick={onExit}>
                <Icon id="qs-eyes-open" size={20} outline />
            </button>
        </div>,
        document.body,
    )
}
